import type { ReactNode } from 'react';
import { GGJLogoFull } from '@shared/components/Brand/GGJLogo';

export type SplitFlowRightVariant = 'light' | 'dark' | 'theme';
export type SplitFlowCoverVariant = 'default' | 'purple';

type Props = {
  coverTitle: string;
  coverSubtitle: string;
  stepLabel: string;
  stepCurrent: number;
  stepTotal: number;
  illustration: ReactNode;
  children: ReactNode;
  variant?: 'full' | 'embedded';
  rightVariant?: SplitFlowRightVariant;
  /** `purple` gives the left panel the brand #8B80F9 gradient treatment */
  coverVariant?: SplitFlowCoverVariant;
  showProgressBar?: boolean;
  progress?: number;
  showLogo?: boolean;
  showFlowAssetsHint?: boolean;
  contentMaxClass?: string;
  /** Tailwind classes for the left-rail illustration block (top-aligned; no flex-grow by default) */
  illustrationAreaClassName?: string;
  /** Optional min-height / layout for step label + title + subtitle so illustration slot stays stable across steps */
  coverMetaBlockClassName?: string;
};

export function SplitFlowShell({
  coverTitle,
  coverSubtitle,
  stepLabel,
  stepCurrent,
  stepTotal,
  illustration,
  children,
  variant = 'full',
  rightVariant = 'theme',
  coverVariant = 'default',
  showProgressBar = false,
  progress,
  showLogo = true,
  showFlowAssetsHint = true,
  contentMaxClass = 'max-w-xl lg:max-w-lg',
  illustrationAreaClassName = 'flex-shrink-0 flex-col py-4 pt-2',
  coverMetaBlockClassName = '',
}: Props) {
  const pct = progress ?? (stepCurrent / stepTotal) * 100;
  const outerMin = variant === 'full' ? 'min-h-screen' : 'min-h-[min(720px,calc(100dvh-9rem))]';

  const rightMainClass =
    rightVariant === 'light'
      ? 'bg-zinc-50 text-zinc-900'
      : rightVariant === 'dark'
        ? 'vscode-bg-primary vscode-text-primary'
        : 'vscode-bg-secondary vscode-text-primary';


  const progressHeaderClass =
    rightVariant === 'light'
      ? 'border-zinc-200 bg-white'
      : 'border-[color-mix(in_srgb,var(--vscode-border-primary)_70%,transparent)] vscode-bg-elevated';
  const progressMetaMuted = rightVariant === 'light' ? 'text-zinc-500' : 'vscode-text-tertiary';
  const progressMetaFaint = rightVariant === 'light' ? 'text-zinc-400' : 'vscode-text-tertiary';
  const progressTrackClass =
    rightVariant === 'light' ? 'bg-zinc-200' : 'bg-[color-mix(in_srgb,var(--vscode-text-tertiary)_22%,transparent)]';

  const isPurple = coverVariant === 'purple';

  const asideBaseClass = 'relative flex shrink-0 flex-col overflow-hidden p-6 sm:p-8 lg:w-[min(42%,500px)] lg:min-h-screen';
  const asideClass = isPurple
    ? asideBaseClass
    : `${asideBaseClass} border-b border-slate-200 bg-slate-100 text-slate-900 dark:border-slate-800/80 dark:bg-[#0c1222] dark:text-white lg:border-b-0 lg:border-r`;

  const asideStyle = isPurple
    ? {
        background: 'linear-gradient(150deg, #4C3FBE 0%, #6654D4 35%, #7B6EE8 65%, #8B80F9 100%)',
        borderRight: '1px solid rgba(139,128,249,0.25)',
      }
    : undefined;

  return (
    <div className={`flex w-full flex-col vscode-bg-primary lg:flex-row ${outerMin}`}>

      {/* ── Left panel ── */}
      <aside className={asideClass} style={asideStyle}>

        {/* decorative glow orbs */}
        <div className="pointer-events-none absolute inset-0 overflow-hidden">
          {isPurple ? (
            <>
              <div className="absolute -top-20 -left-20 h-96 w-96 rounded-full blur-[110px]"
                style={{ background: 'rgba(167,139,250,0.45)' }} />
              <div className="absolute bottom-8 -right-16 h-72 w-72 rounded-full blur-[90px]"
                style={{ background: 'rgba(123,110,232,0.32)' }} />
              <div className="absolute top-[42%] left-[18%] h-56 w-56 rounded-full blur-[80px]"
                style={{ background: 'rgba(139,128,249,0.22)' }} />
            </>
          ) : (
            <>
              <div className="absolute left-8 top-20 h-64 w-64 rounded-full bg-emerald-500 blur-[88px] opacity-[0.12] dark:opacity-[0.14]" />
              <div className="absolute bottom-24 right-2 h-48 w-48 rounded-full bg-teal-400/80 blur-[72px] opacity-[0.12] dark:opacity-[0.14]" />
            </>
          )}
        </div>

        {/* ── logo ── */}
        {showLogo && (
          <div className="relative z-10 mb-8 flex-shrink-0">
            {isPurple ? (
              <>
                {/*
                  GGJLogoFull uses --ggj-logo-mark-bg (#8b80f9) + mix-blend-mode:screen.
                  On a purple panel the screen blend works perfectly — white areas stay white,
                  making the mark clearly visible. Pass explicit white for the text.
                */}
                <GGJLogoFull iconSize={42} fontSize={18} textColor="#fff" className="drop-shadow-sm" />
                <p className="mt-1.5 pl-[2px] text-[12px] font-medium" style={{ color: 'rgba(255,255,255,0.55)' }}>
                  Business Platform
                </p>
              </>
            ) : (
              <>
                <GGJLogoFull iconSize={42} fontSize={18} className="drop-shadow-sm" />
                <p className="mt-1.5 pl-[2px] text-xs text-slate-500 dark:text-slate-500">Business Platform</p>
              </>
            )}
          </div>
        )}

        {/* ── step counter + title ── */}
        <div className={`relative z-10 flex-shrink-0 space-y-2.5 ${coverMetaBlockClassName}`}>
          <p className={`text-[12px] font-bold uppercase tracking-widest ${isPurple ? 'text-white/80' : 'text-emerald-700 dark:text-emerald-400/90'}`}>
            Step {stepCurrent} of {stepTotal}{stepLabel ? ` · ${stepLabel}` : ''}
          </p>
          <h1 className={`text-[26px] font-extrabold leading-tight ${isPurple ? 'text-white' : 'text-slate-900 dark:text-white'}`}>
            {coverTitle}
          </h1>
          <p className={`text-[15px] leading-relaxed max-w-[30ch] ${isPurple ? 'text-white/90' : 'text-slate-600 dark:text-slate-400'}`}>
            {coverSubtitle}
          </p>
        </div>

        {/* ── illustration — directly under step copy; flex spacer below keeps footer at bottom ── */}
        <div className={`relative z-10 flex flex-col items-center justify-start ${illustrationAreaClassName}`}>
          <div className="flex w-full max-w-full items-start justify-center">{illustration}</div>
        </div>

        <div className="relative z-10 min-h-0 flex-1" aria-hidden />

        {/* ── footer ── */}
        <div
          className="relative z-10 flex-shrink-0 space-y-2 pt-4"
          style={{ borderTop: isPurple ? '1px solid rgba(255,255,255,0.15)' : '1px solid rgba(0,0,0,0.08)' }}
        >
          <div className={`flex flex-wrap gap-x-4 gap-y-1 text-[11px] ${isPurple ? 'text-white/70' : 'text-slate-400 dark:text-slate-600'}`}>
            {[
              { label: 'Terms', href: 'https://gograbjob.com/terms' },
              { label: 'Privacy', href: 'https://gograbjob.com/privacy' },
              { label: 'Help', href: 'https://gograbjob.com/help' },
              { label: 'Security', href: 'https://gograbjob.com/security' },
            ].map(({ label, href }) => (
              <a key={label} href={href} target="_blank" rel="noreferrer"
                className={`hover:underline underline-offset-2 transition-colors ${isPurple ? 'hover:text-white' : 'hover:text-slate-700 dark:hover:text-slate-400'}`}>
                {label}
              </a>
            ))}
          </div>
          <p className={`text-[10px] ${isPurple ? 'text-white/55' : 'text-slate-400 dark:text-slate-600'}`}>
            © {new Date().getFullYear()} GoGrabJob Technologies Pvt. Ltd. All rights reserved.
          </p>
        </div>
      </aside>

      {/* ── Right content panel ── */}
      {/*
        backgroundImage inline style adds the gradient on top of vscode-bg-secondary
        without replacing the background-color set by that class.
        Inline styles beat class-level specificity for individual properties.
      */}
      <main
        className={`relative flex min-h-0 min-w-0 flex-1 flex-col ${rightMainClass}`}
        style={
          rightVariant === 'theme'
            ? { backgroundImage: 'linear-gradient(155deg, rgba(139,128,249,0.11) 0%, rgba(139,128,249,0.045) 36%, transparent 62%)' }
            : undefined
        }
      >

        {showProgressBar && (
          <div className={`shrink-0 border-b px-4 py-3 sm:px-6 ${progressHeaderClass}`}>
            <div className={`${contentMaxClass} mx-auto w-full`}>
              <div className="mb-1.5 flex items-center justify-between gap-2">
                <span className={`text-[11px] font-medium uppercase tracking-wide ${progressMetaMuted}`}>{stepLabel}</span>
                <span className={`text-[11px] ${progressMetaFaint}`}>{stepCurrent}/{stepTotal}</span>
              </div>
              <div className={`h-1.5 overflow-hidden rounded-full ${progressTrackClass}`}>
                <div className="h-full rounded-full transition-all duration-300"
                  style={{ width: `${pct}%`, background: '#8B80F9' }} />
              </div>
            </div>
          </div>
        )}

        {/*
          Children are responsible for their own inner layout.
          overflow-y-auto here allows steps/pages with tall content to scroll
          without breaking sticky-header / sticky-footer zones defined by children.
        */}
        <div className={`flex-1 min-h-0 flex flex-col overflow-y-auto`}>
          <div className={`mx-auto w-full ${contentMaxClass} flex flex-col flex-1 min-h-0`}>
            {children}
          </div>
        </div>
      </main>
    </div>
  );
}
